import type {
	DataTableSelectionMultipleChangeEvent,
	DataTableSelectionSingleChangeEvent,
} from "primereact/datatable";
import { useCallback, useEffect } from "react";
import { useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { selectUserFieldTable_Query } from "@relay/selectUserFieldTable_Query.graphql";
import { selectUserFieldTable_QueryFragment$key } from "@relay/selectUserFieldTable_QueryFragment.graphql";
import { selectUserFieldTable_Refetch } from "@relay/selectUserFieldTable_Refetch.graphql";
import { QUERY, QUERY_FRAGMENT } from "./select-user-field-table.graphql";
import { SelectUserFieldTableProps } from "./select-user-field-table.types";
import { Pagination } from "../../../../../components/pagination";
import { MultipleSelectionDataTable } from "../../../../multiple-selection-data-table/multiple-selection-data-table.component";
import { SingleSelectionDataTable } from "../../../../single-selection-data-table/single-selection-data-table.component";

export const SelectUserFieldTable = <T extends string | string[]>({
	isMultipleSelection,
	nameOrEmail,
	accountId,
	fieldValue,
	updateField,
}: SelectUserFieldTableProps<T>) => {
	const query = useLazyLoadQuery<selectUserFieldTable_Query>(QUERY, {
		nameOrEmail,
		accountId,
		first: 10,
	});

	const { data, hasNext, refetch, loadNext } = usePaginationFragment<
		selectUserFieldTable_Refetch,
		selectUserFieldTable_QueryFragment$key
	>(QUERY_FRAGMENT, query);

	const users = data?.Admin?.Auth?.SelectUsers?.edges ?? [];

	const handleRefetch = useCallback(
		(nameOrEmail: string, accountId?: string) => {
			refetch({ nameOrEmail, accountId });
		},
		[refetch],
	);

	const handleSingleSelection = useCallback(
		(event: DataTableSelectionSingleChangeEvent<any>) => {
			updateField(event.value?.node?.id);
		},
		[updateField],
	);

	const handleMultipleSelection = useCallback(
		(event: DataTableSelectionMultipleChangeEvent<any>) => {
			const selectedIds = event.value.map((item: { node: { id: string } }) => item.node.id);
			updateField(selectedIds);
		},
		[updateField],
	);

	const handleLoadNext = () => {
		loadNext(10);
	};

	useEffect(() => {
		handleRefetch(nameOrEmail, accountId);
	}, [nameOrEmail, accountId, handleRefetch]);

	useEffect(() => {
		if (fieldValue && users.find((user) => user?.node.id === fieldValue)) {
			updateField(undefined as unknown as T);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [users]);

	return (
		<>
			{isMultipleSelection ? (
				<MultipleSelectionDataTable
					values={[...users]}
					onSelectionChange={handleMultipleSelection}
					selectedUserIds={Array.isArray(fieldValue) ? fieldValue : []}
				/>
			) : (
				<SingleSelectionDataTable
					values={[...users]}
					onSelectionChange={handleSingleSelection}
					selectedUserId={Array.isArray(fieldValue) ? fieldValue[0] : fieldValue}
				/>
			)}
			<Pagination hasNext={hasNext} loadNext={handleLoadNext} />
		</>
	);
};
